import { api, ApiError } from '../../lib/api'
import { isEnglish } from '../../lib/i18n'

/**
 * إدارة الحوادث السيبرانية — `Features/Admin/SecurityReportsManagement`.
 *
 *   getPage          -> GET  /api/admin/security-reports-management/   is the feature switched on
 *   listReports      -> POST /get-data                                 every report, no paging
 *   listCategories   -> POST /get-categories-data
 *   listStates       -> POST /get-report-states-data
 *   getReport        -> POST /get-data-row              { entryId }
 *   setReportState   -> POST /update-report-state       { entryId, reportState }
 *   setReportsEnabled-> POST /update-report-enabled     { isEnabled }
 *   addComment       -> POST /add-new-report-comment    { securityReportId, comment }
 *   saveCategory     -> POST /add-new-report-category | /update-report-category
 *   deleteCategory   -> POST /delete-report-category    { entryId }
 *
 * THIS SLICE SPEAKS JSON, and it is the first one that does. Every other integrated feature
 * binds its actions from `IFormCollection`; these bind complex types from the request BODY, so a
 * form-encoded post is rejected with **415** before the handler is reached. Property names bind
 * case-insensitively, so the camelCase above is what goes on the wire and PascalCase would work
 * equally well — but the RESPONSE is `{Result, Message}` in PascalCase, always.
 *
 * The page route was retired with this step; the ACTIONS stay on `/admin/...` because the slice
 * has no `apiGroup` of its own, and only the page payload moved to `/api`. API_FINDINGS.md §62.
 */
const BASE = '/admin/security-reports-management'
const PAGE = '/api/admin/security-reports-management'

/** The active language's name, falling back to the other when one side is blank. */
const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/**
 * Every write answers `{Result: bool, Message: string}` with HTTP 200 — including the failures.
 * A category that already exists, a category still in use, a report that vanished: all 200, all
 * `Result: false`, and the message is the only thing that says which.
 *
 * The legacy screen reads `data.result`, lower case, which is `undefined` against this payload —
 * so its add, update, delete, state change and comment all take the failure branch even when the
 * write succeeded. §62. Both casings are accepted here; the API only ever sends one.
 */
const outcome = (data) => ({
  ok: (data?.Result ?? data?.result) === true,
  message: data?.Message ?? data?.message ?? '',
})

const write = async (url, body, signal) => outcome(await api.post(`${BASE}${url}`, body, { signal }))

/**
 * `Title`, `CategoryName`, every comment and most report bodies are run through
 * `HttpUtility.HtmlEncode` server-side.
 *
 * Razor printed them back with `@Html.Raw`, so the round trip cancelled out. React does not:
 * `{row.title}` renders the ENTITIES, and a real report reads
 * `April&#39;s Earth Month &amp; Cybersecurity Spotlights`. 4 of the 107 reports on the demo
 * database are affected, which is exactly the ratio that survives a quick look at the screen.
 *
 * Decoded here, in the service, so no component has to remember. The parser is the browser's own
 * and runs over a detached element that is never inserted into the document, so an encoded
 * `<script>` becomes the TEXT `<script>` — it cannot execute.
 */
function decodeEntities(value) {
  if (!value || !value.includes('&')) return value ?? ''
  const parser = document.createElement('textarea')
  parser.innerHTML = value
  return parser.value
}

/**
 * Rebuilds an attachment link the API cannot build correctly.
 *
 * ```csharp
 * AttachmentURL = $"{baseUrl}root/SecurityReportsAttachments/{a.FileMap}.{a.FileType}"
 * ```
 *
 * `baseUrl` is the `DomainUrl` setting and carries no trailing slash, so what arrives is
 * `https://rawam-demo.comroot/SecurityReportsAttachments/…` — a host that does not exist. Every
 * attachment link in the legacy screen is broken, on every install, unless someone happened to
 * store the domain with a trailing slash.
 *
 * The stored path is recovered from the tail and served from this host, which is where the file
 * actually is. §62.
 */
function attachmentUrl(raw) {
  const tail = String(raw ?? '').match(/root\/SecurityReportsAttachments\/(.+)$/i)
  return tail ? `/root/SecurityReportsAttachments/${tail[1]}` : ''
}

/** `{FileName}.{FileType}` where FileName usually already ends in its extension — `x.eml.eml`. */
const attachmentName = (name) => String(name ?? '').replace(/\.([a-z0-9]+)\.\1$/i, '.$1')

/**
 * A sort key for a date the API already formatted.
 *
 * `DateUtil.FormatDateTime` forces en-US and emits `dd-MM-yyyy hh:mm tt`, so comparing the strings
 * sorts by DAY OF MONTH: "13-08-2025" lands before "29-07-2026", and a column can look sorted
 * while being wrong. The text stays exactly what the server sent — displayed as received, never
 * re-formatted — and this feeds `sortValue` alone.
 *
 * A blank (a report nobody has touched) sorts to the bottom rather than to 1970.
 */
function dateKey(formatted) {
  const parts = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}) (AM|PM)$/i.exec(String(formatted ?? '').trim())
  if (!parts) return 0
  const [, day, month, year, hour, minute, meridiem] = parts
  const hour24 = (Number(hour) % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
  return Date.UTC(Number(year), Number(month) - 1, Number(day), hour24, Number(minute))
}

/* ------------------------------------------------------------------ *
 * Reading
 * ------------------------------------------------------------------ */

/**
 * Whether the feature is switched on at all.
 *
 * This is the ONLY thing that answers it — no action exposes the current value, and the page
 * payload is the page query, which also returns all 107 reports to say so. The list itself is
 * read from `/get-data`, so only the flag is taken from here.
 */
export async function getPage(signal) {
  const dto = (await api.get(`${PAGE}/`, { signal })) ?? {}
  return {
    // Stored as the STRING "true"/"false" in Settings, and handed over as one.
    enabled: String(dto.IsReportsEnabled ?? 'false').toLowerCase() === 'true',
    isArabic: Boolean(dto.IsArabic),
  }
}

/**
 * Every report, in one response, newest first — there is no paging and no filter on this
 * endpoint. 107 rows here; the table filters and pages in the browser, which is what the legacy
 * did and what the shape of the endpoint requires.
 */
export async function listReports(signal) {
  const rows = (await api.post(`${BASE}/get-data`, undefined, { signal })) ?? []

  return rows.map((dto) => ({
    id: dto.Id,
    title: decodeEntities(dto.Title),
    category: decodeEntities(dto.CategoryName),
    /* Pre-formatted `dd-MM-yyyy hh:mm tt` server-side, and "" — not a min-date — when a report
       has never been touched. Displayed as received (W6); never re-parsed. */
    createdOn: dto.CreationDateString ?? '',
    lastUpdate: dto.LastUpdateDateString ?? '',
    // For sorting only — see dateKey. The two strings above are what the screen prints.
    createdAt: dateKey(dto.CreationDateString),
    updatedAt: dateKey(dto.LastUpdateDateString),
    reporter: dto.Username ?? '',
    email: dto.Email ?? '',
    userId: dto.UserId ?? null,
    /* `StateText` is localised for Arabic and is the RAW ENUM NAME for English — the handler has
       no English labels. So the enum name is the key and the label comes from strings.*.js,
       which is the only place all four read properly in both languages. */
    state: dto.StateTextEn ?? '',
  }))
}

/** The category tree — one level of nesting, `parentId` null at the top. */
export async function listCategories(signal) {
  const rows = (await api.post(`${BASE}/get-categories-data`, undefined, { signal })) ?? []

  return rows.map((dto) => ({
    id: dto.Id,
    parentId: dto.ParentId ?? null,
      nameAr: decodeEntities(dto.ArabicName ?? ''),
      nameEn: decodeEntities(dto.EnglishName ?? ''),
      name: decodeEntities(label(dto.ArabicName, dto.EnglishName)),
      parentName: decodeEntities(dto.ParentName ?? ''),
  }))
}

/**
 * The four states a report can be in.
 *
 * Fetched rather than hard-coded so a state added server-side appears without a release — but
 * `DisplayText` is only a label in Arabic (in English it repeats the enum name), so the screen
 * prefers its own string for a state it knows and falls back to this for one it does not.
 */
export async function listStates(signal) {
  const rows = (await api.post(`${BASE}/get-report-states-data`, undefined, { signal })) ?? []
  return rows.map((dto) => ({ id: dto.ValueText ?? '', label: dto.DisplayText ?? '' }))
}

/**
 * One report, with its attachments and its comment thread.
 *
 * A report that does not exist comes back as **HTTP 500** with `detail: "Report not found"`, not
 * a 404 — and an omitted `entryId` binds 0 and lands in the same place. Callers get an ApiError
 * either way; the screen says "not found" rather than "server error".
 */
export async function getReport(id, signal) {
  const dto = await api.post(`${BASE}/get-data-row`, { entryId: Number(id) }, { signal })
  if (!dto) throw new ApiError('Report not found', { code: 'REPORT_NOT_FOUND' })

  return {
    id: dto.Id,
    number: dto.ReportNumber,
    createdOn: dto.CreationDate ?? '',
    lastUpdate: dto.LastUpdateDate ?? '',
    /* The LOCALISED state, and only that — the detail response carries no enum name, so the
       state control is seeded from the row the list already holds. */
    stateText: dto.State ?? '',
    stateCode: dto.StateCode ?? '',
    categoryId: dto.CategoryId ?? null,
    subCategoryId: dto.SubCategoryId ?? null,
    isClosed: Boolean(dto.IsClosed),
    title: decodeEntities(dto.Title),
    /* NOT decoded, and not rendered as markup either. For category 1 the handler skips the
       encoding and hands over the reported email's raw source; for every other category the
       content IS encoded. Since the raw case is attacker-supplied by definition — it is the
       phishing message someone forwarded — the screen shows it as monospaced TEXT rather than
       trusting either branch. Decoding here would undo the protection on the encoded branch. */
    content: dto.ReportContent ?? '',
    contentIsRaw: Boolean(dto.IsDownloadable),
    reporter: dto.Username ?? '',
    category: decodeEntities(dto.SecurityReportCategory),
    subCategory: decodeEntities(dto.SecurityReportSubCategory),
    /* `IsDownloadable` is `SecurityReportCategoryId == 1` — it does not describe the attachments
       at all, it says the report is a forwarded email whose body is downloadable as one. */
    canDownload: Boolean(dto.IsDownloadable),
    attachments: (dto.Attachments ?? []).map((file) => ({
      name: attachmentName(file.Name),
      url: attachmentUrl(file.AttachmentURL),
    })),
    comments: (dto.Comments ?? []).map((comment, index) => ({
      id: index,
      author: comment.Username ?? '',
      body: decodeEntities(comment.Comment),
      date: comment.Date ?? '',
    })),
  }
}

/* ------------------------------------------------------------------ *
 * Writing
 * ------------------------------------------------------------------ */

/**
 * Moves a report to another state — and NOTIFIES ITS REPORTER.
 *
 * The handler queues a `Security_Report_State` notification batch addressed to the user (or to
 * the bare email the report came from) on every call, so this is not a quiet field edit. The
 * screen says so before it is pressed.
 *
 * The state string is written through UNVALIDATED: `report.State = request.ReportState`, with no
 * check against the enum. Only a value from `listStates` is ever sent.
 */
export const setReportState = (id, state, signal, categoryId, subCategoryId) =>
  write('/update-report-state', { entryId: Number(id), reportState: state,
    ...(categoryId ? { categoryId: Number(categoryId), subCategoryId: subCategoryId ? Number(subCategoryId) : null } : {}) }, signal)

/**
 * Switches the whole feature on or off.
 *
 * ASKING FOR `true` CAN STORE `false`. With no categories defined the handler writes "false",
 * returns `Result: false` and explains why — so the switch must follow the RESPONSE rather than
 * the click, or the UI shows a feature as enabled that the database says is off.
 */
export const setReportsEnabled = (on, signal) =>
  write('/update-report-enabled', { isEnabled: Boolean(on) }, signal)

export const addComment = (id, comment, signal) =>
  write('/add-new-report-comment', { securityReportId: Number(id), comment: comment ?? '' }, signal)

/**
 * Adds or updates a category. `parentId` 0 and null both mean "top level".
 *
 * A name that already exists — in EITHER language, against ANY other category — is refused with
 * `Result: false`.
 */
export const saveCategory = (values, signal) => {
  const id = Number(values.id) || 0
  const body = {
    arabicName: values.nameAr ?? '',
    englishName: values.nameEn ?? '',
    parentId: Number(values.parentId) || null,
  }
  return id > 0
    ? write('/update-report-category', { id, ...body }, signal)
    : write('/add-new-report-category', body, signal)
}

/**
 * Deletes a category AND EVERY SUB-CATEGORY UNDER IT, permanently.
 *
 * Refused with a clear message if the category or any of its children is used by a report, which
 * is the only guard there is — nothing else is checked and there is no soft delete.
 *
 * On success the API answers "تمت الإضافة بنجاح" / "Added Successfully": the handler's success
 * string was copied from AddCategory and never changed. That message is dropped rather than
 * shown, because it states something that did not happen; the refusal message is accurate and IS
 * shown. §62.
 */
export const deleteCategory = async (id, signal) => {
  const result = await write('/delete-report-category', { entryId: Number(id) }, signal)
  return result.ok ? { ok: true, message: '' } : result
}

/* ------------------------------------------------------------------ *
 * Certificate validation — `Features/Admin/CertificateSettings/ValidateCertificate`.
 *
 *   validateCode -> POST /CertificateSettings/Validate   { Code }
 *
 * `CertificateValidationPage` shares this module only because it shares the folder.
 * ------------------------------------------------------------------ */

/**
 * One serial, resolved by the server against the print history.
 *
 * It was resolved against `src/data/incidents.js`, a snapshot of `CertificatePrintHistory`
 * compiled into the bundle — so every certificate printed after the snapshot, the twelve-character
 * serials among them, read as forged (Taiga #1544, E2E_FINDINGS F-CERT-1). `Success: false` is "never
 * issued" and answers null; a failed request throws, so it is never reported as a forgery.
 */
export async function validateCode(code) {
  const data = await api.post('/CertificateSettings/Validate', { Code: code.trim().toUpperCase() })
  const row = data?.Success ? data.Data : null
  if (!row) return null
  return {
    identifier: row.CertificateIdentifier ?? '',
    printedBy: row.PrintedBy ?? '',
    printedFor: row.PrintedFor ?? '',
    issueDate: row.IssueDate ?? '',
    printedName: row.PrintedName ?? '',
    printedProgram: row.PrintedProgramName ?? '',
    printedCertDate: row.PrintedDate ?? '',
    language: row.PrintLanguage ?? '',
  }
}
